import { Injectable, OnDestroy } from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { forkJoin, merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { FlowStorage } from '@pe/checkout/storage';
import { CheckoutWindow, FlowInterface, FlowStateEnum } from '@pe/checkout/types';

import { SetPaymentComplete } from '../checkout';

import { CloneFlow, InitFlow } from './flow.actions';
import { FlowState } from './flow.state';

@Injectable({
  providedIn: 'root',
})
export class FlowActionHandler implements OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly loader = (window as CheckoutWindow).pe_pageFlowLoader;

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly flowStorage: FlowStorage,
  ) {
    this.initLoader();

    const cloneFlow$ = forkJoin({
      flow: this.store.selectOnce(FlowState.flow),
      action: this.actions$.pipe(
        ofActionSuccessful(CloneFlow),
      ),
    }).pipe(
      tap(({ flow, action }) => {
        if (!action.payload.skipData) {
          const clonedFlowId = this.store.selectSnapshot(FlowState.flowId);
          this.flowStorage.clone(flow.id, clonedFlowId);
        }
      }),
    );

    merge(
      cloneFlow$,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initLoader(): void {
    const initFlow = (flowData: FlowInterface) => {
      this.store.dispatch(new InitFlow(flowData));
      const isFlowHasFinishedPayment = Boolean(
        flowData
        && [FlowStateEnum.FINISH, FlowStateEnum.CANCEL].indexOf(flowData.state) >= 0
      );
      isFlowHasFinishedPayment && this.store.dispatch(new SetPaymentComplete());
    };

    if (this.loader?.flowData) {
      initFlow(this.loader.flowData);
    } else if (this.loader) {
      this.loader.successCallback = (data: FlowInterface) => {
        initFlow(data);
      };

      this.loader.errorCallback = (message) => {
        this.handleError();
      };

      if (this.loader.lastError) {
        this.handleError();
      }
    }
  }

  private handleError(): void {
    return;
  }
}
